import { Button, useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';

import { ClientsView } from './ClientsView.js';
import { useClientsQuery } from './useClientsQuery.js';

/**
 * The `/clients` route component. Reads the typed `?q` search param, fires the
 * coarse list query, and renders `ClientsView` on success. Loading → skeleton
 * rows; error → announced error state with a retry (the estimates/invoices
 * 3-state contract).
 *
 * The search is server-side: `?q` rides to the endpoint, the SPA never re-filters.
 */

const routeApi = getRouteApi('/clients');

export function ClientsRoute() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const query = useClientsQuery(search);

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-8 text-center sm:p-16"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('clients:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('clients:error.body')}</p>
        {/* The kit's own secondary Button, not a hand-rolled copy of it. The copy
            that used to sit here had drifted: weight 500 against the kit's 600, no
            press feedback, no disabled handling. A retry on an error screen is the
            one control a reader will reach for, so it wears the same clothes as
            every other secondary action in the app. */}
        <Button type="button" variant="secondary" onClick={() => void query.refetch()}>
          {t('clients:error.retry')}
        </Button>
      </section>
    );
  }

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? items.length;

  return <ClientsView clients={items} total={total} search={search} loading={query.isPending} />;
}
